// single-spot — slot-layout primitive (ASK-129 ruling 2, the `featured-comic` cutover).
//
// THE FIXTURE'S OWN SOCKET SEATS THE ONE ITEM. This primitive contributes NO slots, and that is
// its whole content — not an omission.
//
// `featured-comic`'s manifest declares one slot (`showcase`) whose display variant names
// `layoutAlgorithm: "single-spot"`, while the CATALOGUE entry declares the socket that actually
// describes the placement: `SHOWCASE`, `domain: "point"`, `distribution: "single"`,
// `pose: "face-out"`, `capacity: 1`, at the GLB's own `SOCKET_SHOWCASE` node (local y 0.4). Two
// descriptions of one placement, and only one of them is measured against the geometry — so the
// socket wins, exactly as it does for the longbox's authored grammar socket (ASK-112 S4) and for
// every socket-driven fixture the per-socket block already seats (pile-table, DECISION-052).
//
// By returning `slotCount: 0` this hands the fixture to that block, which seats through
// `stage._seatTableSocket` → `layoutProducts` → `_buildSocketComic` — the render path Coord verified
// — and whose `featured-hero` branch already wires the seated comic as a wall-comic, so hover
// outline and E-inspect come with it rather than being re-implemented here.
//
// WHY THE MANIFEST LANE COULD NOT SIMPLY BE DELETED: the composer's preflight refuses a fixture
// whose declared algorithm is unregistered, and it refuses it BEFORE any geometry is placed. So an
// unregistered `single-spot` did not merely skip the products — it took the whole fixture out of
// the composer, and the stand fell through to `_renderCatalogueInstances`, which clones the body
// and seats nothing. MEASURED on the deployed plane 2026-09-21, `ci-probe-shop`, an honest customer
// identity, with the server-side pin `met` and the feed seat present
// (`FEATURED_COMIC_ANCHOR_01 · SHOWCASE · slot 1 · via:"pin"`):
//
//     RENDER   the pedestal + acrylic stand, drawn, with an EMPTY display panel
//     SCENE    0 meshes carrying a seat stamp for that anchor
//
// A merchant's featured comic was invisible to their customers. Registering the name the manifest
// already uses is what lets the fixture into the pipeline that was always going to seat it.

import { registerSlotLayout } from "./index.js";

/**
 * @param {Object} ctx  SlotLayoutContext — `anchor` is all this primitive needs.
 * @returns {Object} an empty slot set, with the same extras shape every primitive returns.
 */
function computeSingleSpot({ anchor }) {
	return {
		slots: [],
		extras: {
			// `anchor.position` is the stand's base. Nothing here may claim to know where the item
			// stands: that is the socket's answer, composed to world by `socketsForAnchor`, and a
			// second guess at it is how one lane ends up drawing where the box isn't.
			laneCenter:     anchor.position.clone(),
			laneSize:       { x: 0, y: 0, z: 0 },
			slotStepVector: null,
			slotQuaternion: null,
			heroExtract:    anchor.position.clone(),
			maxSlots:       1,   // what the fixture HOLDS (manifest `count.fixed: 1`, socket capacity 1)
			slotCount:      0,   // what THIS primitive placed — the socket places the item
			socketSeated:   true,   // …and says so, so "0 slots" is never read as "nothing to seat"
		},
	};
}

registerSlotLayout("single-spot", computeSingleSpot);
export { computeSingleSpot };
